/**
 * BiomeService - Manages biome data and classifications
 */

import { GeoDataService } from './geoData.js';

export class BiomeService {
  constructor() {
    this.biomeData = null;
    this.geoDataService = new GeoDataService();
    this.biomeColors = {
      // Original biomes
      'Tropical': '#76c893',
      'Temperate': '#94bfe1',
      'Desert': '#e2b96a',
      'Polar': '#f4f4f4',
      'Aquatic': '#4a90e2',
      'Mountain': '#8b5a3c',
      'Grassland': '#90ee90',
      'Tundra': '#d3d3d3',

      // Enhanced biomes
      'Tropical Rainforest': '#228B22',
      'Tropical Savanna': '#9ACD32',
      'Tropical Wetland': '#008B8B',
      'Hot Desert': '#F4A460',
      'Cold Desert': '#D2B48C',
      'Temperate Deciduous Forest': '#6B8E23',
      'Temperate Coniferous Forest': '#2E8B57',
      'Temperate Mixed Forest': '#32CD32',
      'Temperate Desert': '#DEB887',
      'Temperate Grassland': '#7CFC00',
      'Mediterranean': '#9370DB',
      'Taiga': '#006400',
      'Alpine Tundra': '#B0C4DE',
      'Ice Sheet': '#F0F8FF',
      'Marine': '#191970',
      'Subtropical': '#FFE4B5',
      'Mixed': '#A0522D'
    };

    this.biomeDescriptions = {
      'Tropical': 'Warm, wet regions near the equator with lush vegetation and incredible biodiversity. Home to rainforests, jungles, and diverse wildlife.',
      'Temperate': 'Moderate climates with distinct seasons. These regions support deciduous and mixed forests, grasslands, and diverse ecosystems.',
      'Desert': 'Dry regions with scarce rainfall where plants and animals have adapted to conserve water. Includes hot and cold deserts.',
      'Polar': 'Cold regions around the poles characterized by ice, snow, and tundra with short growing seasons and specialized wildlife.',
      'Aquatic': 'Water-based ecosystems including oceans, seas, lakes, and rivers that support marine and freshwater life.',
      'Mountain': 'High-altitude regions with varied climates depending on elevation, featuring specialized plant and animal communities.',
      'Grassland': 'Open areas dominated by grasses with few trees, supporting grazing animals and diverse plant communities.',
      'Tundra': 'Treeless regions with permanently frozen subsoil, short growing seasons, and cold-adapted vegetation.'
    };

    this.biomeFacts = {
      'Tropical': [
        'Tropical rainforests produce 20% of the world\'s oxygen!',
        'A single rainforest tree can be home to over 400 insect species.',
        'Tropical forests contain more than half of all known species on Earth.',
        'Some rainforest plants have been used to make life-saving medicines.',
        'Tropical regions near the equator have almost 12 hours of daylight year-round.'
      ],
      'Temperate': [
        'Temperate forests change dramatically with the seasons, creating beautiful autumn colors.',
        'Many temperate animals hibernate during winter to survive the cold.',
        'Temperate grasslands are often called the "breadbaskets" of the world due to fertile soil.',
        'Oak trees in temperate forests can live for over 1,000 years!',
        'Temperate regions experience four distinct seasons due to Earth\'s tilt.'
      ],
      'Desert': [
        'The Sahara Desert is almost as large as the entire United States!',
        'Some desert plants can store water for months without rain.',
        'Desert animals are often most active during cool nights.',
        'Antarctica is technically the world\'s largest desert because it\'s so dry.',
        'Desert sand can reach temperatures of 150°F (66°C) during the day.'
      ],
      'Polar': [
        'Polar ice reflects sunlight back to space, helping cool our planet.',
        'Some polar animals have antifreeze proteins in their blood!',
        'The Arctic Ocean is covered by sea ice that grows and shrinks seasonally.',
        'Polar bears have black skin under their white fur to absorb heat.',
        'During polar winter, the sun doesn\'t rise for months - it\'s called polar night.'
      ],
      'Aquatic': [
        'Oceans cover more than 70% of Earth\'s surface.',
        'The deepest part of the ocean is deeper than Mount Everest is tall!',
        'Coral reefs support 25% of all marine species despite covering less than 1% of the ocean.',
        'Plankton in the ocean produce most of the oxygen we breathe.',
        'Less than 5% of the ocean has been explored by humans.'
      ],
      'Mountain': [
        'Mountains create their own weather patterns as air rises and cools.',
        'Mount Everest grows about 4 millimeters taller each year!',
        'Different elevations on mountains can have completely different climates.',
        'Mountains are home to more than half of the world\'s biodiversity hotspots.',
        'The "thin air" at high altitudes contains less oxygen, making breathing harder.'
      ],
      'Grassland': [
        'Grasslands store huge amounts of carbon in their root systems underground.',
        'Prairie grass roots can extend 15 feet deep into the soil!',
        'Grasslands support large herds of grazing animals like bison and zebras.',
        'Many grasslands are maintained by natural fires that clear old vegetation.',
        'Grassland soil is some of the most fertile on Earth for growing crops.'
      ],
      'Tundra': [
        'Tundra means "treeless plain" in Finnish.',
        'The tundra\'s permafrost layer can be thousands of years old!',
        'Tundra plants grow in cushions close to the ground to conserve heat.',
        'Caribou migrate hundreds of miles across the tundra each year.',
        'Tundra summers have 24 hours of daylight - called the "midnight sun".'
      ]
    };
  }

  /**
   * Load biome data from various sources
   */
  async loadBiomeData() {
    try {
      console.log('Loading enhanced biome data...');

      // Load enhanced geospatial data
      await this.geoDataService.loadEcoregionsData();

      // Initialize enhanced biome descriptions
      this.initializeEnhancedDescriptions();

      this.biomeData = {
        loaded: true,
        version: '2.0.0',
        lastUpdated: new Date().toISOString(),
        source: 'Enhanced Geographic Classification with Real Data Integration',
        geoDataStats: this.geoDataService.getStats()
      };

      console.log('Enhanced biome data loaded successfully');
    } catch (error) {
      throw new Error(`Failed to load biome data: ${error.message}`);
    }
  }

  /**
   * Initialize enhanced biome descriptions
   */
  initializeEnhancedDescriptions() {
    // Add descriptions for new biome types
    const enhancedDescriptions = {
      'Tropical Rainforest': 'Dense, multi-layered forests near the equator with extraordinary biodiversity. These forests have high rainfall, constant warmth, and complex ecosystems supporting millions of species.',
      'Tropical Savanna': 'Grasslands with scattered trees in tropical regions, supporting large herds of grazing animals. These areas have distinct wet and dry seasons.',
      'Tropical Wetland': 'Water-saturated areas in tropical regions, including marshes, swamps, and floodplains that serve as critical habitat for diverse species.',
      'Hot Desert': 'Extremely arid regions with scorching daytime temperatures and minimal rainfall. Plants and animals show remarkable adaptations to conserve water.',
      'Cold Desert': 'High-altitude or high-latitude deserts with cold winters and hot summers. These areas have sparse vegetation adapted to extreme temperature variations.',
      'Temperate Deciduous Forest': 'Forests dominated by broad-leaved trees that shed their leaves seasonally. Rich soils support diverse understory vegetation.',
      'Temperate Coniferous Forest': 'Evergreen needle-leaved forests in cooler temperate regions. These forests are adapted to shorter growing seasons and acidic soils.',
      'Temperate Mixed Forest': 'Forests containing both deciduous and coniferous trees, creating diverse habitats and supporting varied wildlife communities.',
      'Temperate Desert': 'Arid regions in temperate latitudes with cold winters and hot, dry summers. Vegetation is sparse and specially adapted to water scarcity.',
      'Temperate Grassland': 'Vast grasslands with fertile soils, supporting agriculture and grazing. These areas have moderate rainfall and experience periodic fires.',
      'Mediterranean': 'Regions with mild, wet winters and warm, dry summers. Characterized by shrublands, oak woodlands, and diverse endemic species.',
      'Taiga': 'The world\'s largest terrestrial biome, dominated by coniferous forests. Also known as boreal forest, it spans across northern Canada, Alaska, Russia, and Scandinavia.',
      'Alpine Tundra': 'High-altitude treeless regions with harsh climates, short growing seasons, and specialized plant communities adapted to extreme conditions.',
      'Ice Sheet': 'Permanent ice cover in polar regions, supporting specialized ecosystems and playing crucial roles in global climate regulation.',
      'Marine': 'Ocean and sea environments supporting diverse marine life from microscopic plankton to the largest animals on Earth.',
      'Subtropical': 'Transition zones between tropical and temperate regions, often characterized by humid conditions and mixed vegetation types.',
      'Mixed': 'Complex regions where multiple biome types intersect, creating diverse habitats and unique ecological communities.'
    };

    // Merge with existing descriptions
    this.biomeDescriptions = { ...this.biomeDescriptions, ...enhancedDescriptions };
  }

  /**
   * Get biome classification for given coordinates
   * Uses enhanced geographic classification with real data integration
   */
  getBiomeForCoordinates(lat, lon, elevation = 0) {
    // Use enhanced GeoDataService for classification
    if (this.geoDataService.isReady()) {
      return this.geoDataService.getBiomeForCoordinates(lat, lon, elevation);
    }

    // Fallback to enhanced classification logic if geo data not ready
    return this.fallbackBiomeClassification(lat, lon, elevation);
  }

  /**
   * Fallback biome classification (enhanced from original)
   */
  fallbackBiomeClassification(lat, lon, elevation = 0) {
    const absLat = Math.abs(lat);

    // Mountain regions (elevation-based)
    if (elevation > 1500 || this.isMountainRegion(lat, lon)) {
      if (absLat > 60) return 'Alpine Tundra';
      return 'Mountain';
    }

    // Ice sheets and polar regions
    if (absLat >= 75) {
      return 'Ice Sheet';
    }

    // Tundra regions
    if (absLat >= 60) {
      return 'Tundra';
    }

    // Taiga/Boreal Forest
    if (absLat >= 50 && absLat < 60) {
      if (this.isTaigaRegion(lat, lon)) {
        return 'Taiga';
      }
    }

    // Temperate regions
    if (absLat >= 30 && absLat < 50) {
      if (this.isDesertRegion(lat, lon)) {
        return 'Temperate Desert';
      }
      if (this.isGrasslandRegion(lat, lon)) {
        return 'Temperate Grassland';
      }
      if (this.isMediterraneanRegion(lat, lon)) {
        return 'Mediterranean';
      }
      return 'Temperate Mixed Forest';
    }

    // Subtropical regions
    if (absLat >= 20 && absLat < 30) {
      if (this.isDesertRegion(lat, lon)) {
        return 'Hot Desert';
      }
      return 'Subtropical';
    }

    // Tropical regions
    if (absLat < 20) {
      if (this.isDesertRegion(lat, lon)) {
        return 'Hot Desert';
      }
      if (this.isSavannaRegion(lat, lon)) {
        return 'Tropical Savanna';
      }
      return 'Tropical Rainforest';
    }

    return 'Mixed';
  }

  /**
   * Enhanced mountain region detection
   */
  isMountainRegion(lat, lon) {
    const mountainRanges = [
      // Rocky Mountains
      { latMin: 25, latMax: 60, lonMin: -125, lonMax: -105 },
      // Appalachian Mountains
      { latMin: 25, latMax: 50, lonMin: -85, lonMax: -75 },
      // Andes
      { latMin: -55, latMax: 15, lonMin: -80, lonMax: -65 },
      // Alps
      { latMin: 40, latMax: 50, lonMin: 5, lonMax: 15 },
      // Himalayas
      { latMin: 25, latMax: 40, lonMin: 70, lonMax: 105 },
      // Ural Mountains
      { latMin: 45, latMax: 70, lonMin: 55, lonMax: 65 },
      // Atlas Mountains
      { latMin: 28, latMax: 38, lonMin: -10, lonMax: 5 },
      // Ethiopian Highlands
      { latMin: 5, latMax: 18, lonMin: 35, lonMax: 45 }
    ];

    return mountainRanges.some(range =>
      lat >= range.latMin && lat <= range.latMax &&
      lon >= range.lonMin && lon <= range.lonMax
    );
  }

  /**
   * Desert region detection
   */
  isDesertRegion(lat, lon) {
    const desertRegions = [
      // Sahara
      { latMin: 10, latMax: 35, lonMin: -20, lonMax: 40 },
      // Arabian Peninsula
      { latMin: 12, latMax: 32, lonMin: 34, lonMax: 60 },
      // Gobi/Central Asia
      { latMin: 35, latMax: 50, lonMin: 75, lonMax: 125 },
      // Australian Outback
      { latMin: -35, latMax: -15, lonMin: 110, lonMax: 155 },
      // Southwestern US/Northern Mexico
      { latMin: 25, latMax: 40, lonMin: -125, lonMax: -100 },
      // Patagonian Desert
      { latMin: -55, latMax: -35, lonMin: -75, lonMax: -65 },
      // Kalahari
      { latMin: -30, latMax: -15, lonMin: 15, lonMax: 30 }
    ];

    return desertRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Grassland region detection
   */
  isGrasslandRegion(lat, lon) {
    const grasslandRegions = [
      // Great Plains
      { latMin: 30, latMax: 55, lonMin: -105, lonMax: -95 },
      // Pampas
      { latMin: -40, latMax: -25, lonMin: -65, lonMax: -55 },
      // Eurasian Steppe
      { latMin: 40, latMax: 55, lonMin: 20, lonMax: 120 },
      // African Savanna (northern part)
      { latMin: 5, latMax: 20, lonMin: -20, lonMax: 50 }
    ];

    return grasslandRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Taiga region detection
   */
  isTaigaRegion(lat, lon) {
    return (
      (lat >= 50 && lat <= 70 && lon >= -170 && lon <= -50) || // North American Boreal
      (lat >= 50 && lat <= 70 && lon >= 20 && lon <= 170)     // Eurasian Taiga
    );
  }

  /**
   * Mediterranean region detection
   */
  isMediterraneanRegion(lat, lon) {
    const mediterraneanRegions = [
      // Mediterranean Basin
      { latMin: 30, latMax: 45, lonMin: -10, lonMax: 40 },
      // California
      { latMin: 30, latMax: 42, lonMin: -125, lonMax: -115 },
      // Chile
      { latMin: -40, latMax: -30, lonMin: -75, lonMax: -70 },
      // South Africa (Cape)
      { latMin: -35, latMax: -30, lonMin: 15, lonMax: 25 },
      // Southwest Australia
      { latMin: -35, latMax: -30, lonMin: 115, lonMax: 125 }
    ];

    return mediterraneanRegions.some(region =>
      lat >= region.latMin && lat <= region.latMax &&
      lon >= region.lonMin && lon <= region.lonMax
    );
  }

  /**
   * Savanna region detection
   */
  isSavannaRegion(lat, lon) {
    return (
      // African Savanna
      (lat >= -20 && lat <= 15 && lon >= -20 && lon <= 50 &&
       !this.isDesertRegion(lat, lon)) ||
      // Australian Savanna
      (lat >= -20 && lat <= -10 && lon >= 120 && lon <= 145) ||
      // South American Savanna (Cerrado)
      (lat >= -25 && lat <= -5 && lon >= -60 && lon <= -45)
    );
  }

  /**
   * Get color mapping for biomes
   */
  getBiomeColors() {
    return { ...this.biomeColors };
  }

  /**
   * Get description for a specific biome
   */
  getBiomeDescription(biome) {
    return this.biomeDescriptions[biome] || 'Unknown biome type.';
  }

  /**
   * Get a random fun fact for a specific biome
   */
  getBiomeFunFact(biome) {
    const facts = this.biomeFacts[biome];
    if (!facts || facts.length === 0) {
      return 'This is an interesting biome with unique characteristics!';
    }

    const randomIndex = Math.floor(Math.random() * facts.length);
    return facts[randomIndex];
  }

  /**
   * Get all available biomes
   */
  getAllBiomes() {
    return Object.keys(this.biomeColors);
  }

  /**
   * Get biome statistics for a given area
   */
  getBiomeStats(bounds) {
    if (!bounds) return null;

    const stats = {};
    const sampleSize = 20; // Grid sampling for stats

    const latStep = (bounds.north - bounds.south) / sampleSize;
    const lonStep = (bounds.east - bounds.west) / sampleSize;

    for (let lat = bounds.south; lat < bounds.north; lat += latStep) {
      for (let lon = bounds.west; lon < bounds.east; lon += lonStep) {
        const biome = this.getBiomeForCoordinates(lat, lon);
        stats[biome] = (stats[biome] || 0) + 1;
      }
    }

    // Convert to percentages
    const total = Object.values(stats).reduce((sum, count) => sum + count, 0);
    const percentages = {};

    for (const [biome, count] of Object.entries(stats)) {
      percentages[biome] = Math.round((count / total) * 100);
    }

    return percentages;
  }

  /**
   * Check if biome data is loaded
   */
  isLoaded() {
    return !!this.biomeData;
  }

  /**
   * Get metadata about the biome data
   */
  getMetadata() {
    return this.biomeData ? { ...this.biomeData } : null;
  }

  /**
   * Validate coordinates
   */
  validateCoordinates(lat, lon) {
    return {
      valid: lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180,
      lat: Math.max(-90, Math.min(90, lat)),
      lon: Math.max(-180, Math.min(180, lon))
    };
  }

  /**
   * Get educational quiz questions for a biome
   */
  getQuizQuestions(biome) {
    const questions = {
      'Tropical': [
        {
          question: 'Which characteristic is typical of tropical biomes?',
          options: ['High temperatures year-round', 'Frozen ground', 'Little rainfall', 'No vegetation'],
          correct: 0,
          explanation: 'Tropical biomes are characterized by consistently high temperatures throughout the year.',
          difficulty: 'easy'
        },
        {
          question: 'What percentage of Earth\'s oxygen do tropical rainforests produce?',
          options: ['10%', '20%', '30%', '50%'],
          correct: 1,
          explanation: 'Tropical rainforests produce approximately 20% of the world\'s oxygen through photosynthesis.',
          difficulty: 'medium'
        },
        {
          question: 'Which layer of the rainforest receives the least sunlight?',
          options: ['Canopy', 'Understory', 'Forest floor', 'Emergent layer'],
          correct: 2,
          explanation: 'The forest floor receives less than 2% of sunlight due to the dense canopy above.',
          difficulty: 'medium'
        },
        {
          question: 'How many species of plants and animals live in tropical rainforests?',
          options: ['About 100,000', 'About 1 million', 'Over 5 million', 'Over 50 million'],
          correct: 3,
          explanation: 'Scientists estimate that tropical rainforests contain over 50% of all known species on Earth.',
          difficulty: 'hard'
        }
      ],
      'Tropical Rainforest': [
        {
          question: 'What is the average annual rainfall in a tropical rainforest?',
          options: ['500mm', '1000mm', '2500mm', '5000mm'],
          correct: 2,
          explanation: 'Tropical rainforests typically receive 2000-4000mm of rainfall annually.',
          difficulty: 'medium'
        },
        {
          question: 'Which adaptation helps rainforest plants cope with heavy rainfall?',
          options: ['Thick waxy leaves', 'Drip tips on leaves', 'Deep root systems', 'Small leaves'],
          correct: 1,
          explanation: 'Drip tips help water run off leaves quickly, preventing fungal growth and damage.',
          difficulty: 'hard'
        }
      ],
      'Desert': [
        {
          question: 'How do desert plants typically survive with little water?',
          options: ['They don\'t need water', 'They store water in their tissues', 'They drink ocean water', 'They create their own water'],
          correct: 1,
          explanation: 'Desert plants like cacti have evolved to store water in their thick, fleshy tissues for long periods.',
          difficulty: 'easy'
        },
        {
          question: 'Which is the world\'s largest desert?',
          options: ['Sahara', 'Antarctica', 'Gobi', 'Mojave'],
          correct: 1,
          explanation: 'Antarctica is technically the world\'s largest desert because it receives very little precipitation.',
          difficulty: 'medium'
        },
        {
          question: 'What temperature difference can occur between day and night in deserts?',
          options: ['5°C', '15°C', '30°C', '50°C'],
          correct: 2,
          explanation: 'Desert temperatures can vary by 30°C or more between day and night due to low humidity and cloud cover.',
          difficulty: 'medium'
        },
        {
          question: 'How do fennec foxes stay cool in hot deserts?',
          options: ['They sweat a lot', 'They have large ears for heat dissipation', 'They only eat cold food', 'They swim frequently'],
          correct: 1,
          explanation: 'Fennec foxes have oversized ears that help dissipate heat and keep them cool.',
          difficulty: 'hard'
        }
      ],
      'Hot Desert': [
        {
          question: 'What is the hottest temperature ever recorded in a hot desert?',
          options: ['45°C', '57°C', '70°C', '80°C'],
          correct: 1,
          explanation: 'The highest reliably recorded temperature is 54.4°C (129.9°F) in Death Valley.',
          difficulty: 'hard'
        }
      ],
      'Temperate': [
        {
          question: 'What defines temperate climates?',
          options: ['Hot year-round', 'Cold year-round', 'Distinct seasons', 'No precipitation'],
          correct: 2,
          explanation: 'Temperate climates are characterized by four distinct seasons with moderate temperatures.',
          difficulty: 'easy'
        },
        {
          question: 'Why do deciduous trees lose their leaves in autumn?',
          options: ['To save water', 'To conserve energy in winter', 'Because of diseases', 'To grow new ones'],
          correct: 1,
          explanation: 'Trees shed leaves to conserve energy and water during cold winter months when photosynthesis is inefficient.',
          difficulty: 'medium'
        },
        {
          question: 'Which temperate forest type has the richest soil?',
          options: ['Coniferous forest', 'Deciduous forest', 'Mixed forest', 'Bamboo forest'],
          correct: 1,
          explanation: 'Deciduous forests create rich soil from decomposing leaves that fall each year.',
          difficulty: 'medium'
        }
      ],
      'Temperate Deciduous Forest': [
        {
          question: 'How many layers does a typical deciduous forest have?',
          options: ['2 layers', '3 layers', '4 layers', '5 layers'],
          correct: 2,
          explanation: 'Deciduous forests typically have 4 layers: canopy, understory, shrub layer, and forest floor.',
          difficulty: 'medium'
        }
      ],
      'Grassland': [
        {
          question: 'What prevents forests from growing in grasslands?',
          options: ['Too much rain', 'Not enough rain', 'Too many animals', 'Poor soil'],
          correct: 1,
          explanation: 'Grasslands receive enough rain to support grasses but not enough for large trees.',
          difficulty: 'easy'
        },
        {
          question: 'How deep can prairie grass roots grow?',
          options: ['1 meter', '3 meters', '5 meters', '15 meters'],
          correct: 3,
          explanation: 'Prairie grass roots can extend 15 feet (4.5 meters) deep to access water during dry periods.',
          difficulty: 'hard'
        },
        {
          question: 'What role do fires play in grassland ecosystems?',
          options: ['They destroy the ecosystem', 'They help maintain grasslands', 'They have no effect', 'They only harm animals'],
          correct: 1,
          explanation: 'Natural fires prevent tree growth and return nutrients to the soil, maintaining grassland ecosystems.',
          difficulty: 'medium'
        }
      ],
      'Tundra': [
        {
          question: 'What is permafrost?',
          options: ['Frozen surface ice', 'Permanently frozen soil', 'Frozen lakes', 'Winter snow'],
          correct: 1,
          explanation: 'Permafrost is ground that remains frozen for at least two consecutive years.',
          difficulty: 'easy'
        },
        {
          question: 'Why are tundra plants typically small and low-growing?',
          options: ['Lack of nutrients', 'Strong winds and cold', 'Too much water', 'Animals eat them'],
          correct: 1,
          explanation: 'Plants grow low to the ground to avoid harsh winds and conserve heat in the extreme cold.',
          difficulty: 'medium'
        },
        {
          question: 'What happens during the short tundra summer?',
          options: ['Nothing changes', 'The surface thaws briefly', 'All ice melts', 'Trees start growing'],
          correct: 1,
          explanation: 'During the brief summer, the surface layer thaws but permafrost remains frozen underneath.',
          difficulty: 'medium'
        }
      ],
      'Taiga': [
        {
          question: 'What is another name for the taiga biome?',
          options: ['Tropical forest', 'Boreal forest', 'Temperate forest', 'Cloud forest'],
          correct: 1,
          explanation: 'The taiga is also known as the boreal forest, found in northern regions.',
          difficulty: 'easy'
        },
        {
          question: 'Why do coniferous trees dominate the taiga?',
          options: ['They grow faster', 'They\'re adapted to cold and acidic soil', 'They need less water', 'Animals prefer them'],
          correct: 1,
          explanation: 'Conifers are well-adapted to cold temperatures, short growing seasons, and acidic soil.',
          difficulty: 'medium'
        },
        {
          question: 'What percentage of the world\'s forests does the taiga represent?',
          options: ['10%', '20%', '30%', '40%'],
          correct: 2,
          explanation: 'The taiga makes up about 30% of the world\'s forest area, making it the largest forest biome.',
          difficulty: 'hard'
        }
      ],
      'Mediterranean': [
        {
          question: 'What characterizes Mediterranean climate?',
          options: ['Wet summers, dry winters', 'Dry summers, wet winters', 'Rain year-round', 'No rain ever'],
          correct: 1,
          explanation: 'Mediterranean climates have dry, hot summers and mild, wet winters.',
          difficulty: 'easy'
        },
        {
          question: 'Which plants are typical of Mediterranean regions?',
          options: ['Pine trees', 'Oak trees and shrubs', 'Tropical palms', 'Cacti'],
          correct: 1,
          explanation: 'Mediterranean regions are characterized by evergreen oak trees and drought-resistant shrubs.',
          difficulty: 'medium'
        }
      ],
      'Mountain': [
        {
          question: 'How does temperature change with altitude in mountains?',
          options: ['It increases', 'It decreases', 'It stays the same', 'It varies randomly'],
          correct: 1,
          explanation: 'Temperature decreases by about 6.5°C for every 1000 meters of elevation gain.',
          difficulty: 'easy'
        },
        {
          question: 'What is the tree line in mountains?',
          options: ['Where trees are planted', 'The highest elevation where trees grow', 'The lowest point in mountains', 'Where trees are cut down'],
          correct: 1,
          explanation: 'The tree line is the elevation above which trees cannot survive due to harsh conditions.',
          difficulty: 'medium'
        }
      ],
      'Polar': [
        {
          question: 'What causes polar night and midnight sun?',
          options: ['Ocean currents', 'Earth\'s tilt', 'Mountain shadows', 'Cloud cover'],
          correct: 1,
          explanation: 'Earth\'s 23.5° tilt causes polar regions to have continuous daylight or darkness for months.',
          difficulty: 'medium'
        },
        {
          question: 'Which animals are specially adapted to polar regions?',
          options: ['Lizards and snakes', 'Polar bears and penguins', 'Monkeys and parrots', 'Lions and zebras'],
          correct: 1,
          explanation: 'Polar bears (Arctic) and penguins (Antarctic) have special adaptations for extreme cold.',
          difficulty: 'easy'
        }
      ],
      'Aquatic': [
        {
          question: 'What percentage of Earth\'s surface is covered by oceans?',
          options: ['50%', '60%', '71%', '80%'],
          correct: 2,
          explanation: 'Oceans cover approximately 71% of Earth\'s surface.',
          difficulty: 'easy'
        },
        {
          question: 'Where is most marine life found in the ocean?',
          options: ['Deep ocean floor', 'Surface waters', 'Continental shelves', 'Ocean trenches'],
          correct: 2,
          explanation: 'Continental shelves have the most marine life due to sunlight penetration and nutrient availability.',
          difficulty: 'medium'
        },
        {
          question: 'What creates ocean currents?',
          options: ['Fish swimming', 'Wind and temperature differences', 'Submarine movements', 'Tidal waves'],
          correct: 1,
          explanation: 'Ocean currents are driven by wind patterns, temperature differences, and Earth\'s rotation.',
          difficulty: 'hard'
        }
      ]
    };

    return questions[biome] || [];
  }

  /**
   * Get quiz questions filtered by difficulty
   */
  getQuizQuestionsByDifficulty(biome, difficulty = 'all') {
    const allQuestions = this.getQuizQuestions(biome);

    if (difficulty === 'all') {
      return allQuestions;
    }

    return allQuestions.filter(q => q.difficulty === difficulty);
  }

  /**
   * Get random quiz question for a biome
   */
  getRandomQuizQuestion(biome, difficulty = 'all') {
    const questions = this.getQuizQuestionsByDifficulty(biome, difficulty);

    if (questions.length === 0) {
      return null;
    }

    const randomIndex = Math.floor(Math.random() * questions.length);
    return questions[randomIndex];
  }
}
